<script setup lang="ts">
import {
  hexAlpha,
  mixColor,
  readableOn,
  vividColor,
  type LevelTier,
} from '@/utils/levels';
import { getAssetsUrlForCDN } from '@/utils/template';
import type { LevelUpPhase } from '@/stores/level-up-celebration';

const props = defineProps<{
  phase: LevelUpPhase;
  fromTier: LevelTier | null;
  toTier: LevelTier | null;
  /** Muda a cada execução para remontar os nós e reiniciar os keyframes. */
  runId: number;
}>();

const emit = defineEmits<{ close: [] }>();

const showCharge = computed(() => props.phase === 'charge');
const showBurst = computed(() => props.phase === 'burst');
const showHero = computed(
  () => props.phase === 'hero' || props.phase === 'rewards',
);

/**
 * Fase final. Chamava-se "prêmios" quando listava benefícios; hoje ela só
 * mostra o botão e destrava a saída pelo fundo — o nome da fase na store ficou
 * como está para não mexer no roteiro inteiro.
 */
const showActions = computed(() => props.phase === 'rewards');

/**
 * Clique no fundo só vale DEPOIS que a encenação termina.
 *
 * Antes o primeiro clique pulava pros prêmios, e um clique perdido no meio da
 * tela — que é a área da animação inteira — cortava a subida de elo sem o
 * jogador pedir. Uma festa que dura 6s não pode morrer por esbarrão; quem
 * quiser sair antes tem o X e o Esc, que são gestos deliberados.
 */
function onBackdropClick() {
  if (!showActions.value) return;

  emit('close');
}

const color = computed(() => props.toTier?.color ?? '#1515E5');

/**
 * Tinta do cenário. Os brilhos e raios pedem pigmento, não a cor crua: nos elos
 * claros (Prata, VIP 1) o halo saía leitoso e lavava a cena inteira.
 */
const ink = computed(() => vividColor(color.value));
const halo = computed(() => hexAlpha(ink.value, 0.3));
const haloStrong = computed(() => hexAlpha(ink.value, 0.62));

/**
 * Véu OPACO. Com stops translúcidos o cartão do jogador e a barra de navegação
 * atravessavam a celebração, e esse fantasma é o que dava o ar de desbotado.
 */
const veil = computed(
  () =>
    `radial-gradient(78% 64% at 50% 42%,${mixColor(ink.value, '#03060f', 0.76)} 0%,#04060f 46%,#01020a 100%)`,
);

/**
 * Emblemas resolvidos AQUI, junto com os ícones: o store monta os elos com
 * `LEVEL_TIERS`, que guarda o caminho local cru de propósito. Sem passar pelo
 * resolver, o emblema aponta pra fora do CDN de estáticos e a arte não pinta —
 * era o herói faltando na revelação.
 */
const heroBadge = computed(() =>
  props.toTier ? getAssetsUrlForCDN(props.toTier.badge) : '',
);

const previousBadge = computed(() =>
  props.fromTier ? getAssetsUrlForCDN(props.fromTier.badge) : '',
);

const heroName = computed(() => props.toTier?.name ?? '');

const kicker = computed(() =>
  props.toTier?.isVip ? 'VOCÊ ENTROU NO VIP' : 'VOCÊ SUBIU DE ELO',
);

/** Fagulhas do carregamento da barra. */
const embers = computed(() =>
  Array.from({ length: 20 }, (_, i) => ({
    left: `${(i * 17 + 5) % 100}%`,
    size: `${2 + ((i * 3) % 4)}px`,
    blur: `${5 + ((i * 3) % 6)}px`,
    duration: `${1.4 + ((i * 7) % 9) * 0.12}s`,
    delay: `${((i * 11) % 17) * 0.09}s`,
    color: i % 3 === 0 ? (props.toTier?.accent ?? color.value) : color.value,
  })),
);

/** Estilhaços que saem do centro no estouro. */
const sparks = computed(() =>
  Array.from({ length: 22 }, (_, i) => {
    const angle = (i / 22) * Math.PI * 2;
    const distance = 200 + (i % 5) * 52;

    return {
      size: `${4 + ((i * 3) % 5)}px`,
      blur: `${8 + ((i * 5) % 10)}px`,
      dx: `${Math.cos(angle) * distance}px`,
      dy: `${Math.sin(angle) * distance}px`,
      duration: `${0.9 + ((i * 7) % 6) * 0.1}s`,
      delay: `${((i * 5) % 9) * 0.03}s`,
      color: i % 2 === 0 ? color.value : (props.toTier?.accent ?? color.value),
    };
  }),
);

/** Confete subindo na revelação. */
const confetti = computed(() =>
  Array.from({ length: 34 }, (_, i) => ({
    left: `${(i * 23 + 3) % 100}%`,
    width: `${4 + ((i * 3) % 5)}px`,
    height: `${8 + ((i * 5) % 9)}px`,
    radius: i % 3 === 0 ? '50%' : '2px',
    blur: `${4 + ((i * 3) % 7)}px`,
    duration: `${2.2 + ((i * 7) % 11) * 0.14}s`,
    delay: `${((i * 13) % 19) * 0.06}s`,
    color: i % 3 === 0 ? (props.toTier?.accent ?? color.value) : color.value,
  })),
);

// Fecha no Esc — a celebração cobre a tela inteira e precisa de saída por teclado.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('close');
}

/** Raiz do overlay — recebe o foco na abertura e ancora o ciclo do Tab. */
const root = ref<HTMLElement | null>(null);

/**
 * Prende o Tab dentro do overlay. Sem isso o foco passeia pela página atrás,
 * que continua lá embaixo de um `fixed inset-0`.
 */
function trapTab(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !root.value) return;

  const focusable = root.value.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
  );

  if (!focusable.length) {
    event.preventDefault();

    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && (active === first || active === root.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function onKeydownRoot(event: KeyboardEvent) {
  onKeydown(event);
  trapTab(event);
}

// Trava o scroll do documento enquanto a celebração cobre a tela.
let previousOverflow = '';

onMounted(() => {
  window.addEventListener('keydown', onKeydownRoot);

  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  root.value?.focus();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydownRoot);
  document.body.style.overflow = previousOverflow;
});
</script>

<template>
  <!--
    `select-none` e `dragstart` barrado valem para a árvore toda, como na página
    de níveis: a celebração é vitrine, não texto para copiar, e arrastar o
    emblema para fora quebra a composição. Aqui isso é obrigatório e não
    cosmético — o emblema virou `<img>`, que o browser deixa arrastar por padrão.
  -->
  <div
    v-if="toTier"
    :key="runId"
    ref="root"
    :aria-label="`Você subiu para o elo ${heroName}`"
    aria-live="assertive"
    aria-modal="true"
    class="levels-lu-veil fixed inset-0 z-[999998] select-none overflow-hidden focus:outline-none"
    role="dialog"
    :style="{ background: veil }"
    tabindex="-1"
    @click="onBackdropClick"
    @dragstart.prevent
  >
    <!--
      Saída visível desde o primeiro frame. Antes o único jeito de sair nas três
      primeiras fases era a tecla Esc — que não existe no celular.
    -->
    <button
      aria-label="Fechar"
      class="absolute right-4 top-4 z-[2] flex h-10 w-10 items-center justify-center rounded-full text-[18px] leading-none text-white/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] backdrop-blur-sm"
      style="background: rgba(0, 7, 23, 0.55)"
      type="button"
      @click.stop="emit('close')"
    >
      ✕
    </button>

    <!-- Fase 1: barra carregando com o elo antigo em cena -->
    <div
      v-if="showCharge"
      class="absolute inset-0 flex flex-col items-center justify-center gap-9 p-10"
    >
      <span
        aria-hidden="true"
        class="levels-lu-glow pointer-events-none absolute left-1/2 top-1/2 h-[760px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        :style="{ background: `radial-gradient(${halo},transparent 62%)` }"
      ></span>

      <img
        :alt="`Emblema do elo ${fromTier?.name ?? ''}`"
        class="levels-lu-bob relative h-[min(190px,24vh)] w-[min(190px,24vh)] object-contain"
        decoding="async"
        draggable="false"
        :src="previousBadge"
        :style="{ filter: `drop-shadow(0 0 34px ${haloStrong})` }"
      />

      <div
        class="relative flex w-[760px] max-w-[86vw] flex-col items-center gap-[18px]"
      >
        <div
          class="relative h-[34px] w-full rounded-full bg-white/10 p-[5px] shadow-[inset_0_2px_6px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.07)]"
        >
          <div
            class="levels-lu-fill relative h-full overflow-hidden rounded-full"
            :style="{
              background: `linear-gradient(90deg,${color},${mixColor(color, '#ffffff', 0.4)})`,
              boxShadow: `0 0 26px ${haloStrong},0 0 68px ${haloStrong},inset 0 1px 0 rgba(255,255,255,.5)`,
            }"
          >
            <span
              class="levels-lu-shine absolute bottom-0 left-0 top-0 w-[26%]"
              style="
                background: linear-gradient(
                  100deg,
                  transparent,
                  rgba(255, 255, 255, 0.62),
                  transparent
                );
              "
            ></span>
          </div>
        </div>

        <span
          v-for="(ember, index) in embers"
          :key="index"
          aria-hidden="true"
          class="levels-lu-ember pointer-events-none absolute bottom-11 rounded-full"
          :style="{
            left: ember.left,
            width: ember.size,
            height: ember.size,
            background: ember.color,
            boxShadow: `0 0 ${ember.blur} ${ember.color}`,
            animationDuration: ember.duration,
            animationDelay: ember.delay,
          }"
        ></span>

        <span
          class="relative whitespace-nowrap text-base font-bold leading-[21px] tracking-[0.02em] text-white"
          :style="{ textShadow: `0 0 22px ${haloStrong}` }"
        >
          Barra completa
        </span>
        <span
          class="relative whitespace-nowrap text-[12.5px] font-medium uppercase leading-[17px] tracking-[0.14em] text-white/60"
        >
          Subindo para o {{ heroName }}
        </span>
      </div>
    </div>

    <!-- Fase 2: estouro -->
    <template v-if="showBurst">
      <div
        aria-hidden="true"
        class="levels-lu-flash pointer-events-none absolute inset-0"
        :style="{
          background: `radial-gradient(60% 60% at 50% 50%,#fff,${haloStrong} 46%,transparent 72%)`,
        }"
      ></div>
      <span
        v-for="shock in 3"
        :key="shock"
        aria-hidden="true"
        class="levels-lu-shock pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] rounded-full"
        :style="{
          boxShadow: `0 0 0 3px ${shock % 2 ? color : toTier.accent}`,
          animationDelay: `${(shock - 1) * 0.12}s`,
        }"
      ></span>
    </template>

    <!-- Fase 3 e 4: revelação do elo novo + prêmios -->
    <div
      v-if="showHero"
      class="absolute inset-0"
    >
      <span
        aria-hidden="true"
        class="levels-lu-rays pointer-events-none absolute left-1/2 top-[44%] h-[1180px] w-[1180px] rounded-full"
        :style="{
          background: `repeating-conic-gradient(from 0deg,${hexAlpha(ink, 0.34)} 0 2.2deg,transparent 2.2deg 8.6deg)`,
          maskImage:
            'radial-gradient(#000 4%,rgba(0,0,0,.55) 32%,transparent 66%)',
          WebkitMaskImage:
            'radial-gradient(#000 4%,rgba(0,0,0,.55) 32%,transparent 66%)',
        }"
      ></span>
      <span
        aria-hidden="true"
        class="levels-lu-glow pointer-events-none absolute left-1/2 top-[44%] h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        :style="{
          background: `radial-gradient(${haloStrong},${halo} 40%,transparent 68%)`,
        }"
      ></span>

      <!--
        Base escura sob a metade de baixo. Os raios passam atrás do texto e do
        cartão de prêmios; sem esse degradê a leitura briga com o cenário.
      -->
      <span
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-0 bottom-0 h-[58%]"
        style="
          background: linear-gradient(
            180deg,
            transparent,
            rgba(3, 6, 15, 0.55) 38%,
            rgba(3, 6, 15, 0.88) 100%
          );
        "
      ></span>

      <span
        v-for="(piece, index) in confetti"
        :key="`c${index}`"
        aria-hidden="true"
        class="levels-lu-confetti pointer-events-none absolute -bottom-6"
        :style="{
          left: piece.left,
          width: piece.width,
          height: piece.height,
          borderRadius: piece.radius,
          background: piece.color,
          boxShadow: `0 0 ${piece.blur} ${piece.color}`,
          animationDuration: piece.duration,
          animationDelay: piece.delay,
        }"
      ></span>
      <span
        v-for="(spark, index) in sparks"
        :key="`s${index}`"
        aria-hidden="true"
        class="levels-lu-spark pointer-events-none absolute left-1/2 top-[44%] rounded-full"
        :style="{
          width: spark.size,
          height: spark.size,
          background: spark.color,
          boxShadow: `0 0 ${spark.blur} ${spark.color}`,
          '--lu-dx': spark.dx,
          '--lu-dy': spark.dy,
          animationDuration: spark.duration,
          animationDelay: spark.delay,
        }"
      ></span>

      <!--
        Coluna do conteúdo, separada das artes de fundo. `min-h-full` com
        `justify-center` centraliza quando sobra espaço e CRESCE quando falta:
        num container de scroll, `justify-center` puro empurra o topo pra fora
        do alcance — é o que decapitava o emblema em tela baixa.
      -->
      <div class="absolute inset-0 overflow-y-auto">
        <div
          class="relative flex min-h-full flex-col items-center justify-center px-8 py-7"
        >
          <span
            class="levels-lu-hero relative h-[min(340px,40vh)] w-[min(340px,40vh)] flex-none"
          >
            <!--
              `img` e não `background-image`: o emblema é O assunto da tela. Como
              elemento de verdade ele reserva o espaço, o navegador registra a
              falha no Network e um caminho quebrado aparece — em `background` a
              arte sumia sem deixar rastro, e a revelação ficava sem herói.
            -->
            <img
              :alt="`Emblema do elo ${heroName}`"
              class="levels-lu-bob block h-full w-full object-contain"
              decoding="async"
              draggable="false"
              fetchpriority="high"
              :src="heroBadge"
              :style="{
                filter: `drop-shadow(0 0 24px ${hexAlpha(ink, 0.9)}) drop-shadow(0 0 70px ${color})`,
              }"
            />
          </span>

          <span
            class="levels-lu-fade1 relative mt-1.5 whitespace-nowrap text-[12px] font-bold uppercase leading-4 tracking-[0.3em]"
            :style="{
              color: mixColor(ink, '#ffffff', 0.74),
              textShadow: `0 2px 12px rgba(0,0,0,.7)`,
            }"
          >
            {{ kicker }}
          </span>
          <span
            class="levels-lu-title relative mt-1.5 text-center text-[clamp(42px,7.6vh,68px)] font-extrabold leading-[1.08] tracking-[-0.04em] text-white"
            :style="{
              textShadow: `0 0 46px ${haloStrong},0 3px 18px rgba(0,0,0,.75)`,
            }"
          >
            {{ heroName }}
          </span>
          <span
            class="levels-lu-fade2 relative mt-2 max-w-[460px] text-center text-sm font-normal leading-[21px] text-white/[0.88] [text-shadow:0_2px_10px_rgba(0,0,0,.7)]"
          >
            {{ toTier.pitch }}
          </span>

          <!--
            A fase final não lista mais prêmio nenhum: a celebração anuncia o
            elo, não bônus concreto. Sobrou o "Continuar", que é o que libera a
            saída junto com o clique no fundo.

            Fica no DOM desde o primeiro quadro, invisível, em vez de entrar por
            `v-if`: a coluna é `justify-center`, então montar o bloco no fim
            recentralizava tudo de um quadro pro outro e o emblema dava um
            tranco pra cima — o salto de layout é que fazia a entrada parecer
            dura, não a curva. Reservado o espaço, sobra só o fade.

            O botão sai da ordem de tabulação enquanto isso via `disabled`, que
            é o mesmo filtro que o `trapTab` usa pra achar o que é focável.
          -->
          <div
            class="levels-lu-actions relative mt-7 flex w-[560px] max-w-[88vw] flex-col"
            :class="{ 'is-in': showActions }"
            @click.stop
          >
            <button
              class="rounded-xl px-[18px] py-3.5 text-[13.5px] font-bold leading-[18px] tracking-[-0.01em]"
              :disabled="!showActions"
              :style="{
                background: `linear-gradient(180deg,${mixColor(ink, '#ffffff', 0.28)},${ink})`,
                color: readableOn(ink),
                boxShadow: `inset 0 1px 0 rgba(255,255,255,.4),0 12px 30px -12px ${ink}`,
              }"
              type="button"
              @click="emit('close')"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* Toda a sequência é keyframe: o main.scss zera `transition` no app inteiro. */
@keyframes levelsLuVeil {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.levels-lu-veil {
  animation: levelsLuVeil 0.4s ease both;
}

@keyframes levelsLuFill {
  0% {
    width: 6%;
  }
  100% {
    width: 100%;
  }
}

/* 3,1s casa com o `PHASE_SCRIPT` da store: a barra chega no fim e ainda sobra
   meio segundo de barra cheia antes do estouro. */
.levels-lu-fill {
  animation: levelsLuFill 3.1s cubic-bezier(0.3, 0.8, 0.25, 1) both;
}

@keyframes levelsLuShine {
  0% {
    transform: translateX(-140%);
  }
  100% {
    transform: translateX(420%);
  }
}

.levels-lu-shine {
  animation: levelsLuShine 1.15s linear infinite;
}

@keyframes levelsLuBob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-9px);
  }
}

.levels-lu-bob {
  animation: levelsLuBob 3.4s ease-in-out infinite;
}

@keyframes levelsLuGlow {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

.levels-lu-glow {
  animation: levelsLuGlow 2.6s ease-in-out infinite;
}

@keyframes levelsLuFlash {
  0% {
    opacity: 0;
  }
  14% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.levels-lu-flash {
  animation: levelsLuFlash 0.58s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

@keyframes levelsLuShock {
  0% {
    opacity: 0.85;
    transform: translate(-50%, -50%) scale(0.15);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(2.6);
  }
}

.levels-lu-shock {
  animation: levelsLuShock 0.7s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

@keyframes levelsLuRays {
  from {
    transform: translate(-50%, -50%) rotate(0);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.levels-lu-rays {
  transform: translate(-50%, -50%);
  animation: levelsLuRays 30s linear infinite;
}

@keyframes levelsLuHero {
  0% {
    opacity: 0;
    transform: scale(0.24) rotate(-18deg);
  }
  52% {
    opacity: 1;
    transform: scale(1.18) rotate(4deg);
  }
  72% {
    transform: scale(0.96) rotate(-1deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
}

.levels-lu-hero {
  animation: levelsLuHero 1.1s cubic-bezier(0.2, 0.85, 0.25, 1) both;
}

/*
 * A metade de baixo NÃO se desloca — nem o texto, nem o botão. Todos nascem na
 * posição final e só acendem.
 *
 * Antes o kicker, o título e o pitch subiam 26–28px e o botão entrava parado.
 * O tranco não era de nenhum dos dois sozinho: era o bloco inteiro se mexer e
 * o último item chegar sem se mexer. Igualar por baixo (todo mundo parado) sai
 * mais limpo do que igualar por cima, porque o gesto do emblema já carrega a
 * energia da revelação e o texto competia com ele.
 *
 * O título perdeu junto a animação de `letter-spacing`, que era o único trecho
 * daqui que reflowava a linha a cada quadro — 68px de fonte re-medidos 40x.
 *
 * A escada de atrasos fica: acender em sequência ainda dá ordem de leitura
 * (kicker → elo → pitch) sem precisar mover nada.
 */
@keyframes levelsLuFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.levels-lu-title {
  animation: levelsLuFade 0.72s ease-out 0.34s both;
}

.levels-lu-fade1 {
  animation: levelsLuFade 0.5s ease-out 0.22s both;
}

.levels-lu-fade2 {
  animation: levelsLuFade 0.5s ease-out 0.52s both;
}

/*
 * O botão usa o MESMO `levelsLuFade` do texto — mesma natureza, só com atraso
 * maior, porque ele entra numa fase depois. É a coerência que faltava: o que
 * dava coice era o texto subir e ele não.
 *
 * Ele precisa de regra própria, e não só da classe de fade, por causa do
 * repouso: o texto entra montando (a fase `hero` monta a árvore), enquanto o
 * botão fica no DOM desde antes, esperando a fase virar.
 *
 * Nada de `filter` aqui, nem de volta. Esta entrada cai em cima do trecho mais
 * caro da celebração — 34 confetes, 22 estilhaços e os raios em rotação, todos
 * vivos. Um `blur` animado obriga o browser a re-rasterizar um bloco de 560px
 * por cima dessas camadas a cada quadro. Opacidade sozinha o compositor
 * resolve sem repintar nada, que é o que garante que o fade não engasgue.
 *
 * Estado de repouso: ocupa o espaço, mas não aparece nem recebe clique. Sem o
 * `visibility` o botão continuaria clicável embaixo do invisível durante a
 * encenação inteira.
 *
 * `will-change` fica no repouso, NÃO no `.is-in`: promover a camada é trabalho,
 * e pedir isso no mesmo quadro em que a animação começa custa justamente o
 * quadro que se queria economizar. Declarado aqui, a camada já existe pronta
 * quando a classe vira.
 */
.levels-lu-actions {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  will-change: opacity;
}

/* 0,55s: um respiro a mais que o pitch, que fecha em 0,5s. */
.levels-lu-actions.is-in {
  visibility: visible;
  pointer-events: auto;
  animation: levelsLuFade 0.55s ease-out both;
}

@keyframes levelsLuSpark {
  0% {
    opacity: 0;
    transform: translate(0, 0) scale(0.3);
  }
  14% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--lu-dx), var(--lu-dy)) scale(1.15);
  }
}

.levels-lu-spark {
  animation-name: levelsLuSpark;
  animation-timing-function: cubic-bezier(0.2, 0.6, 0.3, 1);
  animation-fill-mode: both;
}

@keyframes levelsLuEmber {
  0% {
    opacity: 0;
    transform: translateY(0) scale(0.6);
  }
  16% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-190px) scale(1.05);
  }
}

.levels-lu-ember {
  animation-name: levelsLuEmber;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes levelsLuConfetti {
  0% {
    opacity: 0;
    transform: translateY(0) rotate(0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-680px) rotate(600deg);
  }
}

.levels-lu-confetti {
  animation-name: levelsLuConfetti;
  animation-timing-function: cubic-bezier(0.2, 0.6, 0.3, 1);
  animation-fill-mode: both;
}
</style>
